/**
 * Mocha Multiple Sessions Library - Main Entry Point
 * Clean architecture implementation with dependency injection and events
 */

import { PublicAPI } from './impl/api/PublicAPI';
import { Container } from './impl/di/Container';
import { ScriptLoader } from './impl/utils/ScriptLoader';

// Create the public API instance
const publicAPI = new PublicAPI();

// ============================================================================
// FUNCTIONAL API (PRIMARY - RECOMMENDED)
// ============================================================================

export const testSessionSetup = publicAPI.setup.bind(publicAPI);
export const getTestEnvironment = publicAPI.getEnvironment.bind(publicAPI);
export const testSession = publicAPI.createSession.bind(publicAPI);
export const runSession = publicAPI.runSession.bind(publicAPI);
export const runAllSessions = publicAPI.runAllSessions.bind(publicAPI);
export const getSession = publicAPI.getSession.bind(publicAPI);
export const getAllSessions = publicAPI.getAllSessions.bind(publicAPI);

// Event API
export const onSessionEvent = publicAPI.onSessionEvent.bind(publicAPI);
export const onAnySessionEvent = publicAPI.onAnySessionEvent.bind(publicAPI);
export const offSessionEvent = publicAPI.offSessionEvent.bind(publicAPI);
export const getEventEmitter = publicAPI.getEventEmitter.bind(publicAPI);

// ============================================================================
// OBJECT-ORIENTED API (ADVANCED USAGE)
// ============================================================================

// Create wrapper classes that use the container
export class SessionManager {
  private container: Container;

  constructor(options: any = {}) {
    this.container = Container.getInstance();
  }

  async createSession(label: string, setupFn: any, options?: any) {
    return publicAPI.createSession(label, setupFn, options);
  }

  async createSessions(configs: Array<{label: string; setupFn: any; options?: any}>) {
    const results = [];
    for (const config of configs) {
      await this.createSession(config.label, config.setupFn, config.options);
      results.push(new ManagedSession(config.label));
    }
    return results;
  }

  getSession(label: string) {
    const session = publicAPI.getSession(label);
    return session ? new ManagedSession(label) : null;
  }

  getAllSessions() {
    return publicAPI.getAllSessions().map(s => new ManagedSession(s.label));
  }

  async runAll(options: { parallel?: boolean } = {}) {
    return publicAPI.runAllSessions();
  }

  getStats() {
    const sessions = publicAPI.getAllSessions();
    return {
      total: sessions.length,
      completed: sessions.filter(s => s.completed).length,
      pending: sessions.filter(s => !s.completed).length,
      failed: sessions.filter(s => s.error).length
    };
  }
}

export class ManagedSession {
  constructor(public readonly label: string) {}

  get exists() {
    return publicAPI.getSession(this.label) !== null;
  }

  get completed() {
    const session = publicAPI.getSession(this.label);
    return session?.completed ?? false;
  }

  get failed() {
    const session = publicAPI.getSession(this.label);
    return !!session?.error;
  }

  get error() {
    const session = publicAPI.getSession(this.label);
    return session?.error;
  }

  get id() {
    const session = publicAPI.getSession(this.label);
    return session?.id;
  }

  get options() {
    const session = publicAPI.getSession(this.label);
    return session?.options;
  }

  async run(options: any = {}) {
    const result = await publicAPI.runSession(this.label);
    return {
      ...result,
      label: this.label,
      passes: result.stats.passes,
      failures: result.stats.failures,
      tests: result.stats.tests
    };
  }

  async runWithTimeout(timeoutMs: number) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Session '${this.label}' execution timeout after ${timeoutMs}ms`));
      }, timeoutMs);

      this.run().then(
        (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      );
    });
  }

  onEvent(eventType: string, callback: any) {
    publicAPI.onSessionEvent(eventType as any, callback);
    return () => publicAPI.offSessionEvent(eventType as any, callback);
  }

  onAnyEvent(callback: any) {
    publicAPI.onAnySessionEvent(callback);
    return () => {}; // Simplified implementation
  }
}

export class SessionBuilder {
  private label: string = '';
  private setupFunctions: Array<() => Promise<void> | void> = [];
  private sessionOptions: any = {};
  private scripts: string[] = [];
  private testSuites: Array<() => void> = [];
  private scriptLoader = new ScriptLoader();

  constructor(private sessionManager: SessionManager) {}

  withLabel(label: string) {
    this.label = label;
    return this;
  }

  withOptions(options: any) {
    this.sessionOptions = { ...this.sessionOptions, ...options };
    return this;
  }

  withTimeout(timeoutMs: number) {
    this.sessionOptions.timeout = timeoutMs;
    return this;
  }

  withRetries(retries: number) {
    this.sessionOptions.retries = retries;
    return this;
  }

  withMetadata(key: string, value: any) {
    if (!this.sessionOptions.metadata) {
      this.sessionOptions.metadata = {};
    }
    this.sessionOptions.metadata[key] = value;
    return this;
  }

  loadScript(scriptPath: string) {
    this.scripts.push(scriptPath);
    return this;
  }

  loadScripts(scriptPaths: string[]) {
    this.scripts.push(...scriptPaths);
    return this;
  }

  addTestSuite(suiteFn: () => void) {
    this.testSuites.push(suiteFn);
    return this;
  }

  addAsyncSetup(setupFn: () => Promise<void>) {
    this.setupFunctions.push(setupFn);
    return this;
  }

  addSetup(setupFn: () => void) {
    this.setupFunctions.push(setupFn);
    return this;
  }

  describe(description: string, testFn: () => void) {
    this.testSuites.push(() => {
      (window as any).describe(description, testFn);
    });
    return this;
  }

  it(description: string, testFn: () => void) {
    this.testSuites.push(() => {
      (window as any).it(description, testFn);
    });
    return this;
  }

  async build(): Promise<ManagedSession> {
    if (!this.label) {
      throw new Error('Session label is required');
    }

    const setupFn = async () => {
      // Load scripts first
      for (const scriptPath of this.scripts) {
        await this.scriptLoader.loadScript(scriptPath);
      }

      // Run setup functions
      for (const setup of this.setupFunctions) {
        await setup();
      }

      // Define test suites
      for (const suite of this.testSuites) {
        suite();
      }
    };

    await this.sessionManager.createSession(this.label, setupFn, this.sessionOptions);
    return new ManagedSession(this.label);
  }

  async buildAndRun(): Promise<any> {
    const session = await this.build();
    return await session.run();
  }
}

// ============================================================================
// LEGACY API (DEPRECATED)
// ============================================================================

export class LegacyAPI {
  private scriptLoader = new ScriptLoader();

  async runSession(
    label: string,
    resultKey: string,
    testFiles: string[],
    reportContainerId: string | null = null,
    options: any = {}
  ) {
    const sessionLabel = `legacy_${label}_${Date.now()}`;
    
    await publicAPI.createSession(sessionLabel, async () => {
      for (const file of testFiles) {
        await this.scriptLoader.loadScript(file);
      }
    }, {
      containerId: reportContainerId || 'mocha-report',
      ...options
    });
    
    return publicAPI.runSession(sessionLabel);
  }

  async runMultipleSessions(
    sessions: Array<{label: string; resultKey: string; testFiles: string[]}>,
    containerId: string,
    options: any = {}
  ) {
    for (const session of sessions) {
      const sessionLabel = `legacy_${session.label}_${Date.now()}`;
      
      await publicAPI.createSession(sessionLabel, async () => {
        for (const file of session.testFiles) {
          await this.scriptLoader.loadScript(file);
        }
      }, {
        containerId,
        ...options
      });
    }
    
    return publicAPI.runAllSessions();
  }
}

// For backward compatibility, export legacy functions directly
const legacyAPI = new LegacyAPI();
export const legacyRunSession = legacyAPI.runSession.bind(legacyAPI);
export const legacyRunMultipleSessions = legacyAPI.runMultipleSessions.bind(legacyAPI);

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export { ScriptLoader };

// Convenience exports for utilities
const scriptLoader = new ScriptLoader();
export const loadScript = scriptLoader.loadScript.bind(scriptLoader);
export const loadScripts = scriptLoader.loadScripts.bind(scriptLoader);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

// Core types
export type { 
  ITestSession, 
  TestSessionOptions, 
  TestSessionStats 
} from './interfaces/core/ITestSession';

export type { 
  SessionSetupFunction 
} from './interfaces/core/ISessionManager';

export type { 
  ISessionExecutionResult 
} from './interfaces/core/ISessionExecutor';

// Result types
export type { 
  SessionResult, 
  SessionExecutionResult 
} from './interfaces/results/SessionResult';

// Event types
export type { 
  SessionEventType, 
  SessionEvent 
} from './interfaces/events/SessionEventTypes';

export type { 
  IEventEmitter 
} from './interfaces/events/IEventBus';

// API types
export type { 
  SessionManagerOptions,
  SessionConfig,
  BatchExecutionOptions,
  SessionManagerStats
} from './interfaces/api/ISessionManagerAPI';

export type { 
  SessionExecutionOptions 
} from './interfaces/api/IManagedSession';

// Test environment types
export type { 
  ITestEnvironment 
} from './interfaces/test-environment/ITestEnvironment';

export type { 
  IMochaInstance 
} from './interfaces/test-environment/IMochaInstance';

// Utility types
export type { 
  ScriptLoaderOptions 
} from './interfaces/utils/IScriptLoader';

// ============================================================================
// DEFAULT EXPORT (BACKWARD COMPATIBILITY)
// ============================================================================

const MochaMultipleSessions = {
  // Functional API
  testSessionSetup,
  getTestEnvironment,
  testSession,
  runSession,
  runAllSessions,
  getSession,
  getAllSessions,
  
  // Event API
  onSessionEvent,
  onAnySessionEvent,
  offSessionEvent,
  getEventEmitter,
  
  // Utilities
  loadScript,
  loadScripts,
  
  // Legacy API
  legacyRunSession,
  legacyRunMultipleSessions
};

export default MochaMultipleSessions;
